import { IconRefresh } from "@tabler/icons-react";
import LayoutHR from "layouts/layout-hr";
import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import { getRequest } from "services/api";
import { formatDateToLocal } from "utils/utils";

const AttendanceReportPage = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    // Start date: 26th of previous month
    const startDate = new Date(currentYear, currentMonth - 1, 26);
    // End date: 25th of current month
    const endDate = new Date(currentYear, currentMonth, 25);
    
    return {
      start_date: formatDateToLocal(startDate),
      end_date: formatDateToLocal(endDate),
    };
  });

  useEffect(() => {
    fetchAttendanceReport();
  }, [dateRange]);

  const fetchAttendanceReport = async () => {
    try {
      setLoading(true);
      const queryString = `?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;
      const response = await getRequest(`/ahr/report/attendance${queryString}`);
      if (response.success) {
        setReportData(response.data);
      }
    } catch (error) {
      console.error("Error fetching attendance report:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRefresh = () => {
    fetchAttendanceReport();
  };

  // Calculate attendance rate distribution for chart
  const attendanceRateData = reportData?.employee_attendance?.reduce((acc, employee) => {
    const rate = Math.floor(employee.attendance_percentage / 10) * 10; // Group by 10% intervals
    const label = rate === 0 ? '0%' : `${rate}-${rate + 9}%`;
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {}) || {};

  const attendanceChartOptions = {
    chart: {
      type: "bar",
      height: 300,
      toolbar: {
        show: false,
      },
      fontFamily: "Inter, sans-serif",
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
        columnWidth: '60%',
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: Object.keys(attendanceRateData),
      labels: {
        style: {
          fontFamily: "Inter, sans-serif",
          fontSize: "12px",
        },
      },
    },
    yaxis: {
      title: {
        text: "Number of Employees",
        style: {
          fontFamily: "Inter, sans-serif",
          fontSize: "13px",
        },
      },
      labels: {
        style: {
          fontFamily: "Inter, sans-serif",
          fontSize: "11px",
        },
      },
    },
    colors: ["#206bc4"],
    tooltip: {
      style: {
        fontFamily: "Inter, sans-serif",
      },
      y: {
        formatter: function (value) {
          return value + " employees";
        },
      },
    },
    grid: {
      show: true,
      borderColor: '#f8f9fa',
      strokeDashArray: 2,
    },
  };

  const attendanceChartSeries = [{
    name: "Employees",
    data: Object.values(attendanceRateData),
  }];

  // Daily trend chart
  const dailyTrendChartOptions = {
    chart: {
      type: "area",
      height: 300,
      toolbar: {
        show: false,
      },
      fontFamily: "Inter, sans-serif",
    },
    stroke: {
      curve: "smooth",
      width: 3,
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        opacityFrom: 0.4,
        opacityTo: 0.1,
      },
    },
    xaxis: {
      categories: reportData?.daily_trend?.map(item => {
        const date = new Date(item.date);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }) || [],
      labels: {
        style: {
          fontFamily: "Inter, sans-serif",
          fontSize: "11px",
        },
      },
    },
    yaxis: {
      title: {
        text: "Count",
        style: {
          fontFamily: "Inter, sans-serif",
          fontSize: "13px",
        },
      },
      labels: {
        style: {
          fontFamily: "Inter, sans-serif",
          fontSize: "11px",
        },
      },
    },
    colors: ["#2fb344", "#d63939"],
    tooltip: {
      style: {
        fontFamily: "Inter, sans-serif",
      },
      x: {
        formatter: function(value, { dataPointIndex }) {
          const date = reportData?.daily_trend?.[dataPointIndex]?.date;
          return date ? new Date(date).toLocaleDateString('id-ID', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }) : value;
        }
      },
    },
    legend: {
      position: "top",
      fontFamily: "Inter, sans-serif",
      fontSize: "12px",
    },
    grid: {
      show: true,
      borderColor: '#f8f9fa',
      strokeDashArray: 2,
    },
  };

  const dailyTrendChartSeries = [
    {
      name: "Present",
      data: reportData?.daily_trend?.map(item => item.present_count) || [],
    },
    {
      name: "Absent",
      data: reportData?.daily_trend?.map(item => item.absent_count) || [],
    },
  ];

  if (loading) {
    return (
      <LayoutHR>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Attendance Report</h3>
                </div>
                <div className="card-body">
                  <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </LayoutHR>
    );
  }

  return (
    <LayoutHR>
      <div className="container-fluid">
        {/* Compact Header with Filters */}
        <div className="row mb-3 mt-2">
          <div className="col-12">
            <div className="card">
              <div className="card-body py-3">
                <div className="row align-items-center g-3">
                  <div className="col-md-6">
                    <h3 className="card-title mb-1">Attendance Report</h3>
                    <div className="text-muted small">
                      Period: {new Date(dateRange.start_date).toLocaleDateString('id-ID')} - {new Date(dateRange.end_date).toLocaleDateString('id-ID')}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex gap-2 justify-content-end">
                      <div className="input-group input-group-sm" style={{ width: "auto" }}>
                        <input
                          type="date"
                          className="form-control"
                          value={dateRange.start_date}
                          onChange={(e) => handleDateChange('start_date', e.target.value)}
                          style={{ minWidth: "130px" }}
                        />
                        <span className="input-group-text">to</span>
                        <input
                          type="date"
                          className="form-control"
                          value={dateRange.end_date}
                          onChange={(e) => handleDateChange('end_date', e.target.value)}
                          style={{ minWidth: "130px" }}
                        />
                      </div>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={fetchAttendanceReport}
                      >
                        <IconRefresh size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="row mb-3">
          <div className="col-lg-3">
            <div className="card">
              <div className="card-body text-center">
                <div className="display-5 font-weight-bold text-primary mb-2">
                  {reportData?.summary?.total_employees || 0}
                </div>
                <div className="text-muted">Total Employees</div>
              </div>
            </div>
          </div>
          <div className="col-lg-3">
            <div className="card">
              <div className="card-body text-center">
                <div className="display-5 font-weight-bold text-success mb-2">
                  {reportData?.summary?.total_present || 0}
                </div>
                <div className="text-muted">Total Present</div>
              </div>
            </div>
          </div>
          <div className="col-lg-3 ">
            <div className="card">
              <div className="card-body text-center">
                <div className="display-5 font-weight-bold text-info mb-2">
                  {reportData?.summary?.attendance_rate || 0}%
                </div>
                <div className="text-muted">Attendance Rate</div>
              </div>
            </div>
          </div>
          <div className="col-lg-3">
            <div className="card">
              <div className="card-body text-center">
                <div className="display-5 font-weight-bold text-warning mb-2">
                  {reportData?.summary?.avg_working_hours || 0}
                </div>
                <div className="text-muted">Avg Working Hours</div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts and Details */}
        <div className="row">
          {/* Attendance Rate Distribution */}
          <div className="col-lg-6 mb-3">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Attendance Rate Distribution</h3>
              </div>
              <div className="card-body">
                {Object.keys(attendanceRateData).length > 0 ? (
                  <Chart
                    options={attendanceChartOptions}
                    series={attendanceChartSeries}
                    type="bar"
                    height={300}
                  />
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="ti ti-chart-bar display-4 mb-3"></i>
                    <div>No attendance data available for the selected period</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Daily Trend Chart */}
          <div className="col-lg-6 mb-3">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Daily Attendance Trend</h3>
              </div>
              <div className="card-body">
                {reportData?.daily_trend?.length > 0 ? (
                  <Chart
                    options={dailyTrendChartOptions}
                    series={dailyTrendChartSeries}
                    type="area"
                    height={300}
                  />
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="ti ti-trending-up display-4 mb-3"></i>
                    <div>No daily trend data available for the selected period</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Late Arrivals Table */}
        <div className="row">
          <div className="col-12 mb-3">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Late Arrivals</h3>
              </div>
              <div className="card-body">
                {reportData?.late_arrivals?.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Check-in Time</th>
                          <th>Late By</th>
                          <th>Office Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.late_arrivals.map((late, index) => (
                          <tr key={index}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="me-3">
                                  <div
                                    className="bg-warning rounded-circle d-flex align-items-center justify-content-center text-white"
                                    style={{ width: "35px", height: "35px", fontSize: "14px", fontWeight: "bold" }}
                                  >
                                    {late.user?.name?.charAt(0).toUpperCase()}
                                  </div>
                                </div>
                                <div>
                                  <div className="font-weight-medium">{late.user?.name}</div>
                                  <div className="text-muted small">{late.user?.email}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="font-weight-medium">
                                {new Date(late.check_in).toLocaleTimeString('id-ID', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                              <div className="text-muted small">
                                {new Date(late.check_in).toLocaleDateString('id-ID')}
                              </div>
                            </td>
                            <td>
                              <span className="badge text-white bg-danger">
                                {Math.floor(late.late / 3600)}h {Math.floor((late.late % 3600) / 60)}m
                              </span>
                            </td>
                            <td>
                              <div className="text-muted small">
                                Start: {late.user?.absensi_setting?.office_hour_start_monday || 'Not set'}
                              </div>
                              <div className="text-muted small">
                                Max Late: {late.user?.absensi_setting?.max_late || 0} min
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="ti ti-clock display-4 mb-3"></i>
                    <div>No late arrivals in the selected period</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Employee Attendance Table */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Employee Attendance Details</h3>
              </div>
              <div className="card-body">
                {reportData?.employee_attendance?.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Contact</th>
                          <th className="text-center">Working Days</th>
                          <th className="text-center">Present Days</th>
                          <th className="text-center">Attendance Rate</th>
                          <th className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.employee_attendance.map((employee, index) => (
                          <tr key={index}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="me-3">
                                  <div
                                    className="bg-primary rounded-circle d-flex align-items-center justify-content-center text-white"
                                    style={{ width: "40px", height: "40px", fontSize: "16px", fontWeight: "bold" }}
                                  >
                                    {employee.name.charAt(0).toUpperCase()}
                                  </div>
                                </div>
                                <div>
                                  <div className="font-weight-medium">{employee.name}</div>
                                  <div className="text-muted small">ID: {employee.id.slice(-8)}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="text-muted small">{employee.email}</div>
                              {employee.phone && (
                                <div className="text-muted small">{employee.phone}</div>
                              )}
                            </td>
                            <td className="text-center">
                              <span className="badge bg-secondary text-white">{employee.working_days}</span>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-success text-white">{employee.attendance_count}</span>
                            </td>
                            <td className="text-center">
                              <div className="d-flex align-items-center justify-content-center">
                                <div className="progress" style={{ width: "60px", height: "8px" }}>
                                  <div
                                    className={`progress-bar ${employee.attendance_percentage >= 80 ? 'bg-success' : employee.attendance_percentage >= 60 ? 'bg-warning' : 'bg-danger'}`}
                                    style={{ width: `${employee.attendance_percentage}%` }}
                                  ></div>
                                </div>
                                <span className="ms-2 font-weight-medium">{employee.attendance_percentage}%</span>
                              </div>
                            </td>
                            <td className="text-center">
                              <span className={`badge text-white ${employee.attendance_percentage >= 80 ? 'bg-success' : employee.attendance_percentage >= 60 ? 'bg-warning' : 'bg-danger'}`}>
                                {employee.attendance_percentage >= 80 ? 'Excellent' : employee.attendance_percentage >= 60 ? 'Good' : 'Needs Improvement'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="ti ti-users display-4 mb-3"></i>
                    <div>No employee attendance data available</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </LayoutHR>
  );
};

export default AttendanceReportPage;
